/**
 * Whether the selected own ship can move and fire now, and if not, why (docs/ux.md U-CTX-01,
 * U-WHY-02): the ship panel shows unavailable actions greyed out with this reason. Pure; the
 * rules engine is the only judge, through its queries and `explain`.
 */
import {
  DIRECTION_VECTORS,
  DIRECTIONS,
  explain,
  parseCellId,
  toCellId,
  type GameState,
  type Reason,
} from '@brig/rules-engine';
import type { Hints } from './hints.ts';
import { squadronCandidates } from './turn.ts';

/** Why not, when the engine has no reason of its own: the step the turn has not reached yet. */
export type Note = 'afterWind' | 'chooseToSail' | 'noTargets' | 'noRoom';

export type Ability =
  | { readonly ok: true; readonly n: number }
  | { readonly ok: false; readonly reason: Reason | null; readonly note?: Note };

const no = (reason: Reason | null, note?: Note): Ability =>
  note ? { ok: false, reason, note } : { ok: false, reason };

/**
 * Sailing: `n` is the farthest reachable distance. Before the wind a ship can only be chosen
 * for the squadron (`inSquadron`: chosen already), afterwards only squadron ships sail.
 */
export function moveAbility(state: GameState, id: string, hints: Hints, inSquadron: boolean): Ability {
  const ship = state.ships.find((s) => s.id === id);
  if (!ship || ship.sunk) return no('sunk');
  if (hints.moveCells.size > 0) {
    const from = parseCellId(ship.cell);
    let far = 0;
    for (const cell of hints.moveCells) {
      const p = parseCellId(cell);
      if (from && p) far = Math.max(far, Math.abs(p.x - from.x), Math.abs(p.y - from.y));
    }
    return { ok: true, n: far };
  }
  if (ship.moved) return no('alreadyMoved');
  const side = state.turn.side;
  if (state.turn.phase !== 'spun') {
    if (inSquadron) return no(null, 'afterWind');
    if (squadronCandidates(state).includes(id)) return no(null, 'chooseToSail');
    return no(explain(state, { type: 'declareMoveGroup', side, shipIds: [id] })?.reason ?? null);
  }
  if (!ship.inGroup) return no('notInSquadron');
  // Every neighbour refused: the first reason is the ship's own (becalmed, in dock…) or the sea's.
  const from = parseCellId(ship.cell);
  if (from) {
    for (const d of DIRECTIONS) {
      const v = DIRECTION_VECTORS[d];
      const p = { x: from.x + v.x, y: from.y + v.y };
      if (p.x < 0 || p.y < 0 || p.x >= state.map.width || p.y >= state.map.height) continue;
      const reason = explain(state, { type: 'moveShip', side, shipId: id, to: toCellId(p) })?.reason;
      if (reason) return no(reason);
    }
  }
  return no(null, 'noRoom');
}

/** Firing: `n` is the number of targets in range. */
export function fireAbility(state: GameState, id: string, hints: Hints): Ability {
  const ship = state.ships.find((s) => s.id === id);
  const fort = ship ? undefined : state.forts.find((f) => f.id === id);
  if (ship?.sunk) return no('sunk');
  if (fort?.destroyed) return no('fortDestroyed');
  if (hints.targets.size > 0) return { ok: true, n: hints.targets.size };
  if ((ship ?? fort)?.shotsLeft === 0) return no('noShotsLeft');
  if (ship && ship.dockedOnTurn !== null) return no('cannotFireInDock');
  if (ship && ship.repairingFort !== null) return no('cannotFireOnRepair');
  return no(null, 'noTargets');
}
